import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  Alert
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  DragIndicator as DragIcon,
  Add as AddIcon,
  TextFields as TextIcon,
  Image as ImageIcon,
  Splitscreen as DividerIcon,
  Assignment as FormIcon,
  KeyboardArrowUp,
  KeyboardArrowDown
} from '@mui/icons-material';
import { PageComponent } from '../../types/pages';

interface SectionComponentManagerProps {
  components: PageComponent[];
  onComponentsChange: (components: PageComponent[]) => void;
  onAddComponent: () => void;
}

const SectionComponentManager: React.FC<SectionComponentManagerProps> = ({
  components,
  onComponentsChange,
  onAddComponent
}) => {
  const [editingComponent, setEditingComponent] = useState<PageComponent | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  const getComponentIcon = (type: PageComponent['type']) => {
    switch (type) {
      case 'text':
        return <TextIcon fontSize="small" />;
      case 'image':
        return <ImageIcon fontSize="small" />;
      case 'divider':
        return <DividerIcon fontSize="small" />;
      case 'form':
        return <FormIcon fontSize="small" />;
      default:
        return <TextIcon fontSize="small" />;
    }
  };

  const getComponentLabel = (component: PageComponent) => {
    switch (component.type) {
      case 'text':
        return component.props?.content ? 
          `Text: ${String(component.props.content).substring(0, 30)}...` : 
          'Text Component';
      case 'image':
        return component.props?.alt ? 
          `Image: ${component.props.alt}` : 
          'Image Component';
      case 'form':
        return component.props?.title ? 
          `Form: ${component.props.title}` : 
          'Form Component';
      case 'divider':
        return 'Divider';
      default:
        return 'Component';
    }
  };

  const handleEditComponent = (component: PageComponent) => {
    setEditingComponent(component);
    setEditDialogOpen(true);
  };

  const handleDeleteComponent = (componentId: string) => {
    const updatedComponents = (components || []).filter(c => c.id !== componentId);
    // Reorder remaining components
    const reorderedComponents = updatedComponents.map((c, index) => ({
      ...c,
      order: index
    }));
    onComponentsChange(reorderedComponents);
  };

  const handleMoveComponent = (componentId: string, direction: 'up' | 'down') => {
    const currentIndex = (components || []).findIndex(c => c.id === componentId);
    if (currentIndex === -1) return;

    const newIndex = direction === 'up' ? currentIndex - 1 : currentIndex + 1;
    if (newIndex < 0 || newIndex >= (components || []).length) return;

    const updatedComponents = [...(components || [])];
    [updatedComponents[currentIndex], updatedComponents[newIndex]] = 
    [updatedComponents[newIndex], updatedComponents[currentIndex]];

    // Update order values
    const reorderedComponents = updatedComponents.map((c, index) => ({
      ...c,
      order: index
    }));

    onComponentsChange(reorderedComponents);
  };

  const handleSaveEdit = () => {
    if (!editingComponent) return;

    const updatedComponents = (components || []).map(c => 
      c.id === editingComponent.id ? editingComponent : c
    );
    onComponentsChange(updatedComponents);
    setEditDialogOpen(false);
    setEditingComponent(null);
  };

  const updateEditingComponentProp = (propName: string, value: unknown) => {
    if (!editingComponent) return;
    
    setEditingComponent({
      ...editingComponent,
      props: {
        ...editingComponent.props,
        [propName]: value
      }
    });
  };

  const renderEditDialog = () => {
    if (!editingComponent) return null;

    return (
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Edit {editingComponent.type.charAt(0).toUpperCase() + editingComponent.type.slice(1)} Component
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }}>
            {editingComponent.type === 'text' && (
              <TextField
                label="Text Content"
                value={editingComponent.props?.content || ''}
                onChange={(e) => updateEditingComponentProp('content', e.target.value)}
                fullWidth
                multiline
                rows={3}
                sx={{ mb: 2 }}
              />
            )}
            
            {editingComponent.type === 'image' && (
              <>
                <TextField
                  label="Image URL"
                  value={editingComponent.props?.src || ''}
                  onChange={(e) => updateEditingComponentProp('src', e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Alt Text"
                  value={editingComponent.props?.alt || ''}
                  onChange={(e) => updateEditingComponentProp('alt', e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Width"
                  value={editingComponent.props?.width || '100%'}
                  onChange={(e) => updateEditingComponentProp('width', e.target.value)}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <TextField
                  label="Height"
                  value={editingComponent.props?.height || 'auto'}
                  onChange={(e) => updateEditingComponentProp('height', e.target.value)}
                  fullWidth
                />
              </>
            )}

            {editingComponent.type === 'form' && (
              <TextField
                label="Form Title"
                value={editingComponent.props?.title || ''}
                onChange={(e) => updateEditingComponentProp('title', e.target.value)}
                fullWidth
                sx={{ mb: 2 }}
              />
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSaveEdit} variant="contained">
            Save Changes
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="subtitle2">Components ({(components || []).length})</Typography>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={onAddComponent}
          variant="outlined"
        >
          Add Component
        </Button>
      </Box>

      {(components || []).length === 0 ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          No components added yet. Click "Add Component" to get started.
        </Alert>
      ) : (
        <Paper variant="outlined" sx={{ mb: 2 }}>
          <List dense>
            {(components || [])
              .sort((a, b) => a.order - b.order)
              .map((component, index) => (
                <ListItem key={component.id} divider={index < (components || []).length - 1}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                    <DragIcon fontSize="small" color="action" />
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mr: 2 }}>
                    {getComponentIcon(component.type)}
                  </Box>
                  
                  <ListItemText
                    primary={getComponentLabel(component)}
                    secondary={
                      <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                        <Chip label={component.type} size="small" variant="outlined" />
                        {component.type === 'form' && component.props?.formId ? (
                          <Chip label={`ID: ${String(component.props.formId)}`} size="small" color="primary" />
                        ) : null}
                      </Box>
                    }
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                  
                  <ListItemSecondaryAction>
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                      <IconButton 
                        size="small" 
                        onClick={() => handleMoveComponent(component.id, 'up')}
                        disabled={index === 0}
                      >
                        <KeyboardArrowUp fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleMoveComponent(component.id, 'down')}
                        disabled={index === (components || []).length - 1}
                      >
                        <KeyboardArrowDown fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        onClick={() => handleEditComponent(component)}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton 
                        size="small" 
                        color="error"
                        onClick={() => handleDeleteComponent(component.id)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
          </List>
        </Paper>
      )}

      {renderEditDialog()}
    </Box>
  );
};

export default SectionComponentManager;